import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Clock, Search, Plus, X, Building2, ChevronDown, ChevronRight, Package } from "lucide-react";
import { toast } from "sonner";
import ExportExcelButton from "@/components/ExportExcelButton";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const FIELDS = [
  { name: "name", label: "Distributor Name" },
  { name: "gst_no", label: "GST Number" },
  { name: "phone", label: "Phone (10-digit)" },
  { name: "email", label: "Email" },
  { name: "address_street", label: "Street", col: 2 },
  { name: "address_city", label: "City" },
  { name: "address_state", label: "State" },
  { name: "address_pin", label: "PIN Code" },
];

export default function Distributors() {
  const [tab, setTab] = useState("approved");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map(f => [f.name, ""])));
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [stockByDist, setStockByDist] = useState({}); // user_id → rolls[]
  const [stockLoading, setStockLoading] = useState({});
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/distributors?status=${tab}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const action = async (id, approve) => {
    try {
      await api.post("/distributors/approve", { distributor_id: id, approve });
      toast.success(approve ? "Distributor approved" : "Distributor rejected");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
  };

  const toggleActivation = async (id, currentActive) => {
    const newState = !(currentActive ?? true);
    if (!window.confirm(newState ? "Reactivate this distributor?" : "Deactivate this distributor? They won't be able to log in.")) return;
    try {
      await api.put(`/distributors/${id}/activate`, { is_active: newState });
      toast.success(newState ? "Distributor activated" : "Distributor deactivated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Toggle failed"); }
  };

  const toggleExpand = async (d) => {
    if (expandedId === d.id) { setExpandedId(null); return; }
    setExpandedId(d.id);
    if (!stockByDist[d.user_id]) {
      setStockLoading((s) => ({ ...s, [d.user_id]: true }));
      try {
        const r = await api.get(`/distributors/${d.user_id}/stock`);
        setStockByDist((prev) => ({ ...prev, [d.user_id]: r.data || [] }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load stock");
        setStockByDist((prev) => ({ ...prev, [d.user_id]: [] }));
      } finally {
        setStockLoading((s) => ({ ...s, [d.user_id]: false }));
      }
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    for (const f of FIELDS) if (!form[f.name]) { toast.error(`${f.label} required`); return; }
    setBusy(true);
    try {
      await api.post("/distributors", form);
      toast.success("Distributor created & approved");
      setShowCreate(false);
      setForm(Object.fromEntries(FIELDS.map(f => [f.name, ""])));
      setTab("approved");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Network</div>
          <h1 className="text-3xl tracking-tighter font-medium mt-1">Distributors</h1>
        </div>
        <button data-testid="new-distributor-btn" onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md text-sm">
          <Plus size={14} /> New Distributor
        </button>
      </div>

      <div className="flex justify-end">
        <ExportExcelButton path="/export/distributors" filename="distributors.xlsx"
          testid="export-distributors-btn" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-[#FFFFFF] border border-[#E5E7EB] rounded-md p-1">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                tab === t.key ? "bg-[#E63946] text-white" : "text-[#475569] hover:text-slate-900"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input data-testid="distributor-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name, phone, GST, city..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]" />
        </div>
      </div>

      {loading ? <div className="text-sm text-[#475569]">Loading…</div> :
        items.length === 0 ? (
          <div className="text-center py-16 text-[#64748B] border border-dashed border-[#E5E7EB] rounded-xl">
            <Building2 size={28} className="mx-auto mb-3" /> No {tab} distributors
          </div>
        ) : (
        <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] overflow-hidden">
          <table className="w-full text-sm" data-testid="distributors-table">
            <thead>
              <tr className="text-left border-b border-[#E5E7EB]">
                <th className="px-3 py-3 w-8"></th>
                {["Distributor", "GST", "Phone", "City", "State", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <React.Fragment key={d.id}>
                  <tr className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC]">
                    <td className="px-3 py-3">
                      <button data-testid={`expand-dist-${d.id}`} onClick={() => toggleExpand(d)}
                        className="p-1 rounded hover:bg-[#E5E7EB]/60 text-[#64748B]">
                        {expandedId === d.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{d.name}</div>
                      <div className="text-[11px] text-[#64748B]">{d.email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#475569]">{d.gst_no}</td>
                    <td className="px-4 py-3 font-mono text-xs">+91 {d.phone}</td>
                    <td className="px-4 py-3 text-xs">{d.address_city}</td>
                    <td className="px-4 py-3 text-xs">{d.address_state}</td>
                    <td className="px-4 py-3"><StatusPill status={d.approval_status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 flex-wrap">
                        {d.approval_status === "pending" && (
                          <>
                            <button data-testid={`approve-${d.id}`} onClick={() => action(d.id, true)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 rounded-md hover:bg-[#10B981]/25">
                              <CheckCircle2 size={12} /> Approve
                            </button>
                            <button data-testid={`reject-${d.id}`} onClick={() => action(d.id, false)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#EF4444]/15 text-[#EF4444] border border-[#EF4444]/30 rounded-md hover:bg-[#EF4444]/25">
                              <XCircle size={12} /> Reject
                            </button>
                          </>
                        )}
                        {d.approval_status === "approved" && (
                          <button data-testid={`toggle-active-${d.id}`}
                            onClick={() => toggleActivation(d.id, d.is_active)}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border ${
                              (d.is_active ?? true)
                                ? "bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/30 hover:bg-[#EF4444]/20"
                                : "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 hover:bg-[#10B981]/20"
                            }`}>
                            {(d.is_active ?? true) ? "Deactivate" : "Activate"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === d.id && (
                    <tr className="bg-[#F8FAFC]/60" data-testid={`dist-stock-${d.id}`}>
                      <td colSpan={8} className="px-6 py-4">
                        <StockPanel
                          loading={!!stockLoading[d.user_id]}
                          rolls={stockByDist[d.user_id] || []}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
              <h3 className="text-lg font-medium">New Distributor <span className="text-xs text-[#10B981] ml-2">(auto-approved)</span></h3>
              <button onClick={() => setShowCreate(false)} className="text-[#475569] hover:text-slate-900" data-testid="dist-modal-close"><X size={18} /></button>
            </div>
            <form onSubmit={submitCreate} className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {FIELDS.map(f => (
                  <div key={f.name} className={f.col === 2 ? "sm:col-span-2" : ""}>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">{f.label}</label>
                    <input data-testid={`new-dist-${f.name}`} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      className="mt-1 w-full px-3 py-2.5 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]" />
                  </div>
                ))}
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#E5E7EB]">Cancel</button>
                <button data-testid="new-dist-submit" type="submit" disabled={busy} className="px-5 py-2 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md text-sm font-medium disabled:opacity-60">
                  {busy ? "Creating..." : "Create Distributor"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function StockPanel({ loading, rolls }) {
  if (loading) return <div className="text-xs text-[#64748B]">Loading stock…</div>;
  if (!rolls || rolls.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Package size={14} /> No rolls held.
      </div>
    );
  }
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#64748B] mb-2 flex items-center gap-1">
        <Package size={12} /> Stock — {rolls.length} roll(s)
      </div>
      <div className="rounded border border-[#E5E7EB] overflow-hidden bg-white">
        <table className="w-full text-xs">
          <thead className="bg-[#F8FAFC]">
            <tr>
              {["Roll No", "Product", "State", "Remaining SqFt", "Owner"].map(h => (
                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.16em] text-[#64748B] font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rolls.map(r => (
              <tr key={r.id} className="border-t border-[#E5E7EB]">
                <td className="px-3 py-2 font-mono text-[#E63946]">{r.ppf_code}</td>
                <td className="px-3 py-2">{r.product_name}</td>
                <td className="px-3 py-2"><StatePill state={r.state} /></td>
                <td className="px-3 py-2 font-mono">{r.remaining_sqft ?? 0}</td>
                <td className="px-3 py-2 text-[#64748B]">{r.current_owner_role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatePill({ state }) {
  const cm = { available: "#10B981", allocated: "#F59E0B", partially_used: "#F59E0B", consumed: "#64748B" };
  const c = cm[state] || "#64748B";
  return <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em]"
    style={{ background: c + "22", color: c, border: `1px solid ${c}55` }}>{state}</span>;
}

function StatusPill({ status }) {
  const map = {
    pending: { bg: "#F59E0B", icon: Clock, label: "Pending" },
    approved: { bg: "#10B981", icon: CheckCircle2, label: "Approved" },
    rejected: { bg: "#EF4444", icon: XCircle, label: "Rejected" },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em]" style={{ background: m.bg + "22", color: m.bg, border: `1px solid ${m.bg}55` }}>
      <Icon size={10} /> {m.label}
    </span>
  );
}
